/**
 * «Назначить культуру» для уже выбранных полей посевного плана.
 *
 * Отличие от соседнего `AddCropDialog` (заведение культуры в план) — в том,
 * ЧТО пользователь уже решил, когда открыл диалог. Там он выбирает поля внутри
 * диалога и заводит культуру на свободные. Здесь поля выбраны заранее — строкой
 * таблицы или чекбоксами, — и он видел в таблице, что на них сейчас стоит.
 * Поэтому здесь занятое поле не пропускается, а СМЕНЯЕТ культуру: «поменять
 * пшеницу на подсолнечник» — одно действие агронома, а не пара «сними, потом
 * заведи», между которыми план успевает побыть дырявым.
 *
 * Сколько полей при этом теряют прежнюю культуру — сказано и до нажатия
 * (предупреждением), и после (в итоге). Молчаливая перезапись чужой работы —
 * ровно то, за что систему перестают открывать.
 */
import { useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Skeleton } from '@/components/ui/skeleton';
import { useCropPicker } from '@/components/fields/card/api';
import { useAddCropToVariant, type VariantField } from '@/lib/planning/planCrops';
import { formatHa } from '@/lib/planning/planVariant';
import { matchesQuery, pickableCrops } from '../plan-variant/AddCropDialog';
import { fieldPlanStatus } from './sowingPlan';

/**
 * Сколько выбранных полей сменят культуру. Пар сюда не входит: поле «отдыхает»
 * — это решение агронома, но не работа, которую жалко потерять, и отдельного
 * предупреждения оно не заслуживает.
 */
export function fieldsLosingCrop(
  fields: readonly VariantField[],
  cropId: string | null,
): VariantField[] {
  return fields.filter(
    (f) => fieldPlanStatus(f) === 'assigned' && f.plan_crop_id !== cropId,
  );
}

export function AssignCropDialog({
  scenarioId,
  seasonYear,
  fields,
  open,
  onClose,
  onAssigned,
}: {
  scenarioId: string;
  seasonYear: number;
  /** Поля, которым назначаем культуру — выбраны до открытия диалога. */
  fields: VariantField[];
  open: boolean;
  onClose: () => void;
  /**
   * Культура назначена — отдельно от закрытия. Отмена диалога НЕ должна
   * стирать выбор строк: человек отбирал поля фильтром, и «передумал с
   * культурой» не значит «передумал с полями».
   */
  onAssigned?: () => void;
}) {
  const crops = useCropPicker();
  const assign = useAddCropToVariant();

  const [cropId, setCropId] = useState<string | null>(null);
  const [cropQuery, setCropQuery] = useState('');
  const [yieldCHa, setYieldCHa] = useState('');
  const [yieldError, setYieldError] = useState<string | null>(null);

  // Алфавит + поиск — сквозной паттерн выпадашек-справочников: он предсказуем
  // и одинаков для новичка и старожила, в отличие от «часто используемых».
  const cropOptions = useMemo(() => pickableCrops(crops.data ?? []), [crops.data]);
  const shownCrops = cropOptions.filter((c) => matchesQuery(c.name, cropQuery));
  const cropName = cropOptions.find((c) => c.id === cropId)?.name ?? '';

  const editable = fields.filter((f) => !f.occupied_by_subzones);
  const bySubzones = fields.length - editable.length;
  const losing = fieldsLosingCrop(editable, cropId);
  const area = editable.reduce((sum, f) => sum + f.area_ha, 0);
  const single = fields.length === 1 ? fields[0] : null;

  const close = () => {
    setCropId(null);
    setCropQuery('');
    setYieldCHa('');
    setYieldError(null);
    onClose();
  };

  const submit = () => {
    if (!cropId || editable.length === 0 || assign.isPending) return;
    const trimmed = yieldCHa.trim();
    const parsed = trimmed === '' ? null : Number(trimmed.replace(',', '.'));
    if (parsed !== null && (!Number.isFinite(parsed) || parsed < 0)) {
      // Набранное НЕ выбрасываем молча: назначить культуру без урожайности,
      // когда человек её вводил, — значит «система съела ввод». Строчный
      // редактор таблицы отбивает мусор словами — диалог обязан так же.
      setYieldError('Урожайность — число не меньше нуля, например 52 или 48,5');
      return;
    }
    assign.mutate(
      {
        scenarioId,
        cropId,
        cropName,
        fieldIds: editable.map((f) => f.id),
        plannedYieldCHa: parsed,
        replace: true,
      },
      {
        onSuccess: () => {
          onAssigned?.();
          close();
        },
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && close()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {single ? `Культура на поле «${single.name}»` : 'Культура выбранным полям'}
          </DialogTitle>
          <DialogDescription>
            {single
              ? `Сезон ${seasonYear}. Площадь ${formatHa(single.area_ha)} — по контуру поля, вводить её не нужно.`
              : `Сезон ${seasonYear}. Полей: ${editable.length} · ${formatHa(area)}. Площадь считается по контурам, вводить её не нужно.`}
          </DialogDescription>
        </DialogHeader>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            submit();
          }}
        >
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-2">
              <Label htmlFor="assign-crop-search">Культура из номенклатуры хозяйства</Label>
              <Input
                id="assign-crop-search"
                value={cropQuery}
                onChange={(e) => setCropQuery(e.target.value)}
                placeholder="Поиск по названию"
                autoFocus
              />
              {crops.isLoading && <Skeleton className="h-24 w-full" />}
              {!crops.isLoading && cropOptions.length === 0 && (
                <p className="text-sm text-muted-foreground">
                  В номенклатуре хозяйства нет ни одной культуры, связанной с общим
                  справочником. Заведите культуру в номенклатуру — тогда её можно будет
                  посеять в плане.
                </p>
              )}
              {cropOptions.length > 0 && (
                <ScrollArea className="max-h-56 rounded-md border border-border">
                  <div className="flex flex-col p-1">
                    {shownCrops.map((crop) => (
                      <button
                        key={crop.id}
                        type="button"
                        aria-pressed={crop.id === cropId}
                        onClick={() => setCropId(crop.id)}
                        className={
                          crop.id === cropId
                            ? 'rounded-md bg-primary/10 px-3 py-2 text-left text-sm font-medium text-primary'
                            : 'rounded-md px-3 py-2 text-left text-sm text-foreground hover:bg-accent'
                        }
                      >
                        {crop.name}
                      </button>
                    ))}
                    {shownCrops.length === 0 && (
                      <p className="px-3 py-2 text-sm text-muted-foreground">
                        По запросу ничего не нашлось.
                      </p>
                    )}
                  </div>
                </ScrollArea>
              )}
            </div>

            <div className="flex flex-col gap-2">
              <Label htmlFor="assign-crop-yield">
                Плановая урожайность, ц/га (необязательно)
              </Label>
              <Input
                id="assign-crop-yield"
                inputMode="decimal"
                value={yieldCHa}
                aria-invalid={yieldError ? true : undefined}
                className={yieldError ? 'border-destructive focus-visible:ring-destructive' : undefined}
                onChange={(e) => {
                  setYieldCHa(e.target.value);
                  if (yieldError) setYieldError(null);
                }}
                placeholder="например, 55"
              />
              {yieldError && <p className="text-xs text-destructive">{yieldError}</p>}
              <p className="text-xs text-muted-foreground">
                Без урожайности валовка и выручка по этим полям не посчитаются — их
                поставят прочерком, а не нулём.
              </p>
            </div>

            {losing.length > 0 && (
              <p className="text-sm text-accent-brand">
                {losing.length === 1
                  ? `Поле «${losing[0].name}» сейчас под культурой «${losing[0].plan_crop_name}» — она сменится.`
                  : `Полей сменят культуру: ${losing.length}. Прежние посевы уйдут в архив варианта.`}
              </p>
            )}

            {bySubzones > 0 && (
              <p className="text-sm text-muted-foreground">
                {bySubzones === 1 ? 'Одно поле' : `Полей: ${bySubzones}`} засеяно по зонам —
                их культура правится в карточке поля, отсюда мы их не трогаем.
              </p>
            )}
          </div>

          <DialogFooter className="mt-4">
            <Button type="button" variant="outline" onClick={close}>
              Отмена
            </Button>
            <Button type="submit" disabled={!cropId || editable.length === 0 || assign.isPending}>
              {assign.isPending ? 'Назначаем…' : 'Назначить культуру'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
